import { formatCurrency, formatDecimalUnit } from '@/common/utils/formatters';
import { BoxesIcon, FileBoxIcon } from 'lucide-react';
import { ClientCompanyAllowedCategoriesAndProducts } from '../types/allowedCategoriesAndProducts.type';
import { UseFormWatch } from 'react-hook-form';
import { CompanyBranchValidationSchema } from '../schemas/createBranch.schema';
import { unFormatCurrency } from '@/common/utils/unFormatters';
import { ControlledByType } from '@/features/privateCompanies/types/companyGovernmentAllocation.type';

export class ClientCompanyAllowedCategoriesAndProductsTransformer {
  static optionsTransformer({
    key,
    watch,
    showAvailableTotal = true,
    productCategoryIndex,
    controlledBy = 'currency',
  }: {
    key: 'categories' | 'products'
    watch?: UseFormWatch<CompanyBranchValidationSchema>
    showAvailableTotal?: boolean
    productCategoryIndex?: number
    controlledBy?: ControlledByType
  }) {
    return (categoriesAndProducts: ClientCompanyAllowedCategoriesAndProducts) => {
      if (!categoriesAndProducts || Array.isArray(categoriesAndProducts)) return []

      if (!showAvailableTotal) {
        return Object.entries(categoriesAndProducts?.[key])?.map(([id, data]) => ({
          ...data,
          value: id,
          label: data.display_name,
        }))
      }

      const isQuantityControlled = controlledBy === 'quantity';
      const watchGrantedCredit = watch && watch('granted_credit');
      const watchProductCategoryGrantedCredits = watch && watch('product_category_granted_credits_attributes');

      return Object.entries(categoriesAndProducts?.[key])?.map(([id, data]) => {
        let availableTotal = isQuantityControlled
          ? Number(data.available_quantity)
          : Number(data.available_total);

        if (watch) {
          if (key === 'categories') {
            const productCategoryGrantedCreditsSum = watchProductCategoryGrantedCredits?.reduce((sum, productCategory) => {
              if (isQuantityControlled) {
                const totalQuantity = (productCategory as unknown as { total_quantity?: string }).total_quantity;
                return sum + (Number(totalQuantity) || 0);
              }
              const grantedCredit = (productCategory as unknown as { granted_credit?: string }).granted_credit;
              return sum + (grantedCredit ? unFormatCurrency(grantedCredit) : 0);
            }, 0) || 0;

            const grantedCredit = watchGrantedCredit ? unFormatCurrency(watchGrantedCredit) : 0;
            const grantedCreditLeft = grantedCredit - (productCategoryGrantedCreditsSum || 0) || 0;

            const baseAvailable = isQuantityControlled ? Number(data.available_quantity) : Number(data.available_total);
            availableTotal = baseAvailable
              ? Math.min(baseAvailable, Math.max(grantedCreditLeft, 0))
              : Math.max(grantedCreditLeft, 0);
          } else if (productCategoryIndex !== undefined) {
            const watchProductCategoryGrantedCredit = watch(`product_category_granted_credits_attributes.${productCategoryIndex}`);

            const productGrantedCreditsSum = watchProductCategoryGrantedCredit?.product_granted_credits_attributes?.reduce((sum, product) => {
              if (isQuantityControlled) {
                const totalQuantity = (product as unknown as { total_quantity?: string }).total_quantity;
                return sum + (Number(totalQuantity) || 0);
              }
              const grantedCredit = (product as unknown as { granted_credit?: string }).granted_credit;
              return sum + (grantedCredit ? unFormatCurrency(grantedCredit) : 0);
            }, 0) || 0;

            let productCategoryGrantedCredit: number;
            if (isQuantityControlled) {
              const totalQuantity = (watchProductCategoryGrantedCredit as unknown as { total_quantity?: string }).total_quantity;
              productCategoryGrantedCredit = Number(totalQuantity) || 0;
            } else {
              const grantedCredit = (watchProductCategoryGrantedCredit as unknown as { granted_credit?: string }).granted_credit;
              productCategoryGrantedCredit = grantedCredit ? unFormatCurrency(grantedCredit) : 0;
            }
            const productCategoryGrantedCreditLeft = productCategoryGrantedCredit - (productGrantedCreditsSum || 0) || 0

            const baseAvailable = isQuantityControlled ? Number(data?.available_quantity) : Number(data?.available_total);
            availableTotal = baseAvailable
              ? Math.min(baseAvailable, Math.max(productCategoryGrantedCreditLeft, 0))
              : Math.max(productCategoryGrantedCreditLeft, 0);
          }
        }

        return {
          ...data,
          value: id,
          label: (
            <div className='flex flex-col gap-1'>
              <div className='flex items-center gap-1'>
                {key === 'categories' ? <BoxesIcon strokeWidth={1.5} size={16} /> : <FileBoxIcon strokeWidth={1.5} size={16} />}

                <p>
                  {data.display_name}
                </p>
              </div>

              <div className='flex flex-col gap-px'>
                <p className='italic text-xs text-white-400'>
                  {isQuantityControlled
                    ? `Quantidade disponível: ${formatDecimalUnit(availableTotal || 0, data.unit_for_quantity)}`
                    : `Total disponível: ${formatCurrency(availableTotal || 0)}`
                  }
                </p>
              </div>
            </div>
          ),
          filter: data?.display_name,
        }
      })
    }
  }
}
